import { zodResolver } from '@hookform/resolvers/zod';
import { customersControllerCreate, customersControllerCreateContact } from '@kalba/api-client';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import {
  ArrowLeft,
  Building2,
  Check,
  FileCheck,
  FileText,
  Info,
  Mail,
  Phone,
  Plus,
  Star,
  Upload,
  User,
  Users,
  X,
  Zap,
} from 'lucide-react';
import { useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { FormField } from '../../components/forms/form-field';
import { FormSection } from '../../components/forms/form-section';
import { AppLayout } from '../../components/layout/app-layout';
import { Input } from '../../components/ui/input';
import { PhoneInput } from '../../components/ui/phone-input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { api } from '../../lib/ky';
import { cn } from '../../lib/utils';
import { type CustomerOrgFormValues, customerOrgSchema } from '../../lib/validations';
import { useAuthStore } from '../../stores/auth.store';

export const Route = createFileRoute('/customers/new')({
  beforeLoad: () => {
    const { user, permissions } = useAuthStore.getState();
    if (!user) throw redirect({ to: '/login' });
    if (!permissions.includes('customer:create')) throw redirect({ to: '/dashboard' });
  },
  component: NewCustomerPage,
});

const ACCOUNT_TYPES = [
  { label: 'General Contractor', value: 'GENERAL_CONTRACTOR' },
  { label: 'Real Estate Developer', value: 'REAL_ESTATE_DEVELOPER' },
  { label: 'Architecture Firm', value: 'ARCHITECTURE_FIRM' },
  { label: 'Specialty Contractor', value: 'SPECIALTY_CONTRACTOR' },
  { label: 'Civil Engineering', value: 'CIVIL_ENGINEERING' },
  { label: 'Electrical Contractor', value: 'ELECTRICAL_CONTRACTOR' },
  { label: 'Company', value: 'COMPANY' },
  { label: 'Other', value: 'OTHER' },
];

function NewCustomerPage() {
  return (
    <AppLayout noPadding>
      <NewCustomerContent />
    </AppLayout>
  );
}

function NewCustomerContent() {
  const navigate = useNavigate();
  // File uploads are outside RHF scope
  const [tradeLicenseFile, setTradeLicenseFile] = useState<File | null>(null);
  const [tradeLicenseError, setTradeLicenseError] = useState('');
  const [tradeLicenseDragging, setTradeLicenseDragging] = useState(false);
  const [vatFile, setVatFile] = useState<File | null>(null);
  const [vatError, setVatError] = useState('');
  const [vatDragging, setVatDragging] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<CustomerOrgFormValues>({
    resolver: zodResolver(customerOrgSchema),
    defaultValues: {
      name: '',
      sapCode: '',
      accountType: '',
      primaryEmail: '',
      primaryPhone: '',
      tradeLicenseNo: '',
      vatRegistrationNo: '',
      contacts: [
        { id: undefined, fullName: '', email: '', phone: '', designation: '', isPrimary: true },
      ],
    },
  });

  const {
    fields: contactFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: 'contacts',
  });

  const watchedName = watch('name');
  const watchedEmail = watch('primaryEmail');

  function addContact() {
    append({
      id: undefined,
      fullName: '',
      email: '',
      phone: '',
      designation: '',
      isPrimary: false,
    });
  }

  function removeContact(index: number) {
    remove(index);
  }

  const onSubmit = async (data: CustomerOrgFormValues) => {
    setSubmitting(true);
    try {
      const account = await (customersControllerCreate({
        requestBody: {
          name: data.name.trim(),
          accountType: data.accountType || 'COMPANY',
          sapCode: data.sapCode?.trim() || undefined,
          primaryEmail: data.primaryEmail?.trim() || undefined,
          primaryPhone: data.primaryPhone?.trim() || undefined,
          tradeLicenseNo: data.tradeLicenseNo?.trim() || undefined,
          vatRegistrationNo: data.vatRegistrationNo?.trim() || undefined,
        },
      }) as unknown as Promise<{ id: string }>);

      if (tradeLicenseFile) {
        const fd = new FormData();
        fd.append('file', tradeLicenseFile);
        await api.post(`customers/${account.id}/documents/TRADE_LICENSE`, {
          body: fd,
          headers: {},
        });
      }

      if (vatFile) {
        const fd = new FormData();
        fd.append('file', vatFile);
        await api.post(`customers/${account.id}/documents/VAT_REGISTRATION`, {
          body: fd,
          headers: {},
        });
      }

      for (const c of data.contacts) {
        if (!c.fullName.trim()) continue;
        await (customersControllerCreateContact({
          id: account.id,
          requestBody: {
            fullName: c.fullName.trim(),
            email: c.email?.trim() || '',
            phone: c.phone?.trim() || '',
            designation: c.designation?.trim() || undefined,
            isPrimary: c.isPrimary,
          },
        }) as unknown as Promise<void>);
      }

      navigate({ to: '/customers/$customerId', params: { customerId: account.id } });
    } catch (e: unknown) {
      const body = (e as { body?: { message?: string | string[]; error?: string } })?.body;
      const raw = body?.message ?? (e as { message?: string })?.message;
      const lines = Array.isArray(raw) ? raw : [raw ?? 'Failed to create customer.'];
      toast.error('Failed to create customer', {
        description: lines.join(' · '),
        duration: 6000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const orgInfoDone = !!(watchedName?.trim() && watchedEmail?.trim());
  const docsDone = !!(tradeLicenseFile || vatFile);
  const contactsDone = contactFields.some((_, i) => !!watch(`contacts.${i}.fullName`)?.trim());

  const summaryItems = [
    { label: 'Organization name', done: !!watchedName?.trim() },
    { label: 'Email address', done: !!watchedEmail?.trim() },
    { label: 'SAP code', done: !!watch('sapCode')?.trim() },
    { label: 'Phone number', done: !!watch('primaryPhone')?.trim() },
    { label: 'Trade license', done: !!tradeLicenseFile },
    { label: 'VAT certificate', done: !!vatFile },
    { label: 'Primary contact', done: contactsDone },
  ];

  const docSlots = [
    {
      key: 'trade',
      label: 'Trade License',
      hint: 'Commercial registration document',
      icon: FileText,
      gradient: 'from-emerald-500 to-teal-600',
      file: tradeLicenseFile,
      setFile: setTradeLicenseFile,
      error: tradeLicenseError,
      setError: setTradeLicenseError,
      dragging: tradeLicenseDragging,
      setDragging: setTradeLicenseDragging,
    },
    {
      key: 'vat',
      label: 'VAT Certificate',
      hint: 'Tax registration certificate',
      icon: FileCheck,
      gradient: 'from-blue-500 to-indigo-600',
      file: vatFile,
      setFile: setVatFile,
      error: vatError,
      setError: setVatError,
      dragging: vatDragging,
      setDragging: setVatDragging,
    },
  ];

  return (
    <div className="flex flex-col" style={{ minHeight: 'calc(100vh - 64px)' }}>
      {/* ── Page header ── */}
      <div className="bg-gradient-to-br from-white via-white to-brand-50/30 border-b border-gray-200 px-6 py-5">
        <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-4">
          <button
            onClick={() => navigate({ to: '/customers' })}
            className="hover:text-gray-600 transition-colors"
          >
            Customers
          </button>
          <span>/</span>
          <span className="text-gray-700 font-medium">New Customer</span>
        </div>

        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-brand-500 to-emerald-600 flex items-center justify-center shadow-md shadow-brand-200 shrink-0">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2.5">
                <h1 className="text-xl font-bold text-gray-900">New Customer</h1>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-warning-50 text-warning-700 border border-warning-200">
                  <span className="w-1.5 h-1.5 rounded-full bg-warning-400" /> Draft
                </span>
              </div>
              <p className="text-sm text-gray-500">Create and manage a new organization account</p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => navigate({ to: '/customers' })}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors text-gray-600"
            >
              <ArrowLeft className="w-4 h-4" /> Cancel
            </button>
          </div>
        </div>

        {/* Progress steps */}
        <div className="flex items-center gap-6 mt-5">
          {[
            { label: 'Organization Info', done: orgInfoDone },
            { label: 'Documents', done: docsDone },
            { label: 'Contacts', done: contactsDone },
          ].map((step, i) => (
            <div key={i} className="flex items-center gap-2">
              <div
                className={cn(
                  'w-5 h-5 rounded-full flex items-center justify-center border-2 transition-all',
                  step.done ? 'border-brand-500 bg-brand-500' : 'border-gray-300 bg-white',
                )}
              >
                {step.done && <Check className="w-3 h-3 text-white" />}
              </div>
              <span
                className={cn('text-xs font-medium', step.done ? 'text-gray-900' : 'text-gray-400')}
              >
                {step.label}
              </span>
              {i < 2 && (
                <div className={cn('w-12 h-px mx-1', step.done ? 'bg-brand-200' : 'bg-gray-200')} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ── Two-column body ── */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Left: scrollable form */}
        <div className="flex-1 overflow-y-auto px-6 py-6 pb-8 space-y-5 min-w-0">
          {/* Organization Info */}
          <FormSection
            icon={<Building2 className="w-4 h-4" />}
            iconBg="bg-brand-50 text-brand-500"
            title="Organization Info"
          >
            <div className="grid grid-cols-2 gap-5">
              <FormField
                className="col-span-2"
                label="Organization Name"
                required
                error={errors.name?.message}
              >
                <Input
                  {...register('name')}
                  placeholder="e.g. Acme Construction Inc."
                  hasError={!!errors.name}
                />
              </FormField>

              <FormField label="SAP Code" hint="From ERP system">
                <Input {...register('sapCode')} placeholder="e.g. SAP-00472831" />
              </FormField>

              <FormField label="Company Type">
                <Controller
                  name="accountType"
                  control={control}
                  render={({ field }) => (
                    <Select value={field.value ?? ''} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select type…" />
                      </SelectTrigger>
                      <SelectContent>
                        {ACCOUNT_TYPES.map((t) => (
                          <SelectItem key={t.value} value={t.value}>
                            {t.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </FormField>

              <FormField label="Primary Email" required error={errors.primaryEmail?.message}>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <Input
                    {...register('primaryEmail')}
                    type="email"
                    placeholder="info@company.com"
                    hasError={!!errors.primaryEmail}
                    className="pl-10"
                  />
                </div>
              </FormField>

              <FormField label="Primary Phone" error={errors.primaryPhone?.message}>
                <div className="relative">
                  <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <PhoneInput
                    {...register('primaryPhone')}
                    placeholder="+971 50 123 4567"
                    hasError={!!errors.primaryPhone}
                    className="pl-10"
                  />
                </div>
              </FormField>

              <FormField label="Trade License Number" hint="Identifier, not the document">
                <Input {...register('tradeLicenseNo')} placeholder="e.g. TRN262040224" />
              </FormField>

              <FormField label="VAT Registration Number" hint="Identifier, not the document">
                <Input {...register('vatRegistrationNo')} placeholder="e.g. 100234567800003" />
              </FormField>

              <FormField className="col-span-2" label="Notes">
                <textarea
                  placeholder="Internal notes about this customer…"
                  rows={3}
                  className="w-full rounded-xl border border-gray-300 bg-white px-3.5 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100 transition-all resize-none"
                />
              </FormField>
            </div>
          </FormSection>

          {/* Documents */}
          <FormSection
            icon={<FileText className="w-4 h-4" />}
            iconBg="bg-emerald-50 text-emerald-600"
            title="Documents"
          >
            <div className="grid grid-cols-2 gap-5">
              {docSlots.map((doc) => {
                const DocIcon = doc.icon;
                return (
                  <div key={doc.key}>
                    <p className="text-sm font-medium text-gray-800 mb-1.5">{doc.label}</p>
                    {doc.file ? (
                      <div className="rounded-2xl border border-success-200 bg-success-50 p-4 flex items-center gap-3">
                        <div
                          className={cn(
                            'w-10 h-10 rounded-xl bg-gradient-to-br flex items-center justify-center shrink-0',
                            doc.gradient,
                          )}
                        >
                          <DocIcon className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">
                            {doc.file.name}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">PDF · Uploaded</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            doc.setFile(null);
                            doc.setError('');
                          }}
                          className="w-7 h-7 rounded-lg hover:bg-error-50 flex items-center justify-center text-gray-400 hover:text-error-500 transition-colors shrink-0"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ) : (
                      <div>
                        <label className="block cursor-pointer group">
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            className="sr-only"
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f) {
                                if (f.size > 10 * 1024 * 1024) {
                                  doc.setError('File exceeds 10 MB limit.');
                                  return;
                                }
                                doc.setError('');
                                doc.setFile(f);
                              }
                              e.target.value = '';
                            }}
                          />
                          <div
                            className={cn(
                              'rounded-2xl border-2 border-dashed bg-white transition-all duration-150 p-8 flex flex-col items-center gap-3 text-center',
                              doc.dragging
                                ? 'border-brand-400 bg-brand-50/50 scale-[1.01]'
                                : 'border-gray-200 group-hover:border-brand-300 group-hover:bg-brand-50/30',
                            )}
                            onDragOver={(e) => {
                              e.preventDefault();
                              doc.setDragging(true);
                            }}
                            onDragLeave={() => doc.setDragging(false)}
                            onDrop={(e) => {
                              e.preventDefault();
                              doc.setDragging(false);
                              const f = e.dataTransfer.files?.[0];
                              if (f) {
                                if (f.size > 10 * 1024 * 1024) {
                                  doc.setError('File exceeds 10 MB limit.');
                                  return;
                                }
                                doc.setError('');
                                doc.setFile(f);
                              }
                            }}
                          >
                            <div className="w-12 h-12 rounded-2xl bg-gray-50 group-hover:bg-brand-50 flex items-center justify-center transition-colors">
                              <Upload className="w-6 h-6 text-gray-400 group-hover:text-brand-500 transition-colors" />
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-gray-700">{doc.label}</p>
                              <p className="text-xs text-gray-400 mt-0.5">{doc.hint}</p>
                              <p className="text-xs text-gray-300 mt-1">
                                PDF, JPG or PNG · Max 10MB
                              </p>
                            </div>
                            <span className="text-xs font-semibold text-brand-500 group-hover:text-brand-600">
                              Click or drag to upload
                            </span>
                          </div>
                        </label>
                        {doc.error && <p className="mt-1 text-xs text-error-500">{doc.error}</p>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100">
              <button
                type="button"
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-gray-200 hover:border-brand-300 hover:bg-brand-50/30 text-sm font-medium text-gray-400 hover:text-brand-600 transition-all"
              >
                <Plus className="w-4 h-4" /> Add Another Document
              </button>
            </div>
          </FormSection>

          {/* Contacts */}
          <FormSection
            icon={<Users className="w-4 h-4" />}
            iconBg="bg-warning-50 text-warning-600"
            title="Contacts"
          >
            <div className="space-y-3">
              {contactFields.map((field, idx) => (
                <div
                  key={field.id}
                  className="relative rounded-2xl border border-gray-200 bg-gray-50/60 p-5 space-y-4 hover:border-brand-200 transition-colors group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-500 to-brand-600 flex items-center justify-center shrink-0 shadow-sm">
                        <User className="w-3.5 h-3.5 text-white" />
                      </div>
                      <span className="text-sm font-semibold text-gray-900">Contact {idx + 1}</span>
                      {field.isPrimary && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-brand-50 text-brand-600 border border-brand-100">
                          <Star className="w-2.5 h-2.5" /> Primary
                        </span>
                      )}
                    </div>
                    {idx > 0 && (
                      <button
                        type="button"
                        onClick={() => removeContact(idx)}
                        className="opacity-0 group-hover:opacity-100 flex items-center gap-1 text-xs text-gray-400 hover:text-error-500 transition-all"
                      >
                        <X className="w-3 h-3" /> Remove
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      label="Full Name"
                      required
                      error={errors.contacts?.[idx]?.fullName?.message}
                    >
                      <Input
                        {...register(`contacts.${idx}.fullName`)}
                        placeholder="First and last name"
                        hasError={!!errors.contacts?.[idx]?.fullName}
                      />
                    </FormField>
                    <FormField label="Role / Title">
                      <Input
                        {...register(`contacts.${idx}.designation`)}
                        placeholder="e.g. Project Manager"
                      />
                    </FormField>
                    <FormField label="Email" error={errors.contacts?.[idx]?.email?.message}>
                      <div className="relative">
                        <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        <Input
                          {...register(`contacts.${idx}.email`)}
                          type="email"
                          placeholder="contact@company.com"
                          hasError={!!errors.contacts?.[idx]?.email}
                          className="pl-10"
                        />
                      </div>
                    </FormField>
                    <FormField label="Phone" error={errors.contacts?.[idx]?.phone?.message}>
                      <div className="relative">
                        <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        <PhoneInput
                          {...register(`contacts.${idx}.phone`)}
                          placeholder="+971 50 123 4567"
                          hasError={!!errors.contacts?.[idx]?.phone}
                          className="pl-10"
                        />
                      </div>
                    </FormField>
                  </div>

                  {!field.isPrimary && (
                    <Controller
                      control={control}
                      name={`contacts.${idx}.isPrimary`}
                      render={({ field: f }) => (
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={f.value}
                            onChange={(e) => {
                              if (e.target.checked) {
                                // Unset all others first via manual update
                                contactFields.forEach((_, i) => {
                                  if (i !== idx) {
                                    // We can't call setValue from here easily; use the onChange
                                  }
                                });
                              }
                              f.onChange(e.target.checked);
                            }}
                            className="w-4 h-4 rounded border-gray-300 accent-brand-500 cursor-pointer"
                          />
                          <span className="text-xs font-medium text-gray-500">
                            Set as primary contact
                          </span>
                        </label>
                      )}
                    />
                  )}
                </div>
              ))}

              <button
                type="button"
                onClick={addContact}
                className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl border-2 border-dashed border-gray-200 hover:border-brand-300 hover:bg-brand-50/30 text-sm font-semibold text-gray-400 hover:text-brand-600 transition-all group"
              >
                <div className="w-5 h-5 rounded-full bg-gray-200 group-hover:bg-brand-100 flex items-center justify-center transition-colors">
                  <Plus className="w-3 h-3 group-hover:text-brand-500 transition-colors" />
                </div>
                Add Another Contact
              </button>
            </div>
          </FormSection>
        </div>

        {/* Right: sticky summary panel */}
        <div className="w-72 shrink-0 px-5 py-6 hidden xl:block border-l border-gray-100 bg-gray-50/50">
          <div className="sticky top-6 space-y-4">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100">
                <div className="flex items-center gap-2 mb-0.5">
                  <div className="w-6 h-6 rounded-lg bg-brand-50 flex items-center justify-center">
                    <Zap className="w-3.5 h-3.5 text-brand-500" />
                  </div>
                  <p className="text-sm font-semibold text-gray-900">Account Summary</p>
                </div>
                <p className="text-xs text-gray-400">Live preview as you fill in the form</p>
              </div>
              <div className="p-5 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-brand-500 to-emerald-600 flex items-center justify-center shadow-md shrink-0">
                    {watchedName?.trim() ? (
                      <span className="text-white font-bold text-sm">
                        {watchedName
                          .trim()
                          .split(' ')
                          .filter(Boolean)
                          .slice(0, 2)
                          .map((w) => w[0])
                          .join('')}
                      </span>
                    ) : (
                      <Building2 className="w-5 h-5 text-white/70" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      {watchedName?.trim() || 'Organization Name'}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5 truncate">
                      {watchedEmail?.trim() || 'Email not set'}
                    </p>
                  </div>
                </div>

                <div className="space-y-0.5">
                  {summaryItems.map((item, i) => (
                    <div key={i} className="flex items-center gap-2.5 py-1.5">
                      <div
                        className={cn(
                          'w-4 h-4 rounded-full flex items-center justify-center shrink-0 transition-all',
                          item.done ? 'bg-success-500' : 'bg-gray-200',
                        )}
                      >
                        {item.done && <Check className="w-2.5 h-2.5 text-white" />}
                      </div>
                      <span
                        className={cn(
                          'text-xs',
                          item.done ? 'text-gray-900 font-medium' : 'text-gray-400',
                        )}
                      >
                        {item.label}
                      </span>
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={handleSubmit(onSubmit)}
                  disabled={submitting}
                  className={cn(
                    'w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-semibold rounded-xl transition-all',
                    !submitting
                      ? 'bg-brand-500 text-white hover:bg-brand-600 shadow-sm shadow-brand-200'
                      : 'bg-gray-200 text-gray-500 cursor-not-allowed',
                  )}
                >
                  <Building2 className="w-4 h-4" />
                  {submitting ? 'Creating…' : 'Create Customer'}
                </button>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <Info className="w-4 h-4 text-brand-400" />
                <p className="text-xs font-semibold text-gray-800">Getting started</p>
              </div>
              <p className="text-xs text-gray-500 leading-relaxed">
                Start with the organization name and email — they're required to create the account.
                Documents and contacts can be added later from the customer profile.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
